import { useQuery } from '@tanstack/react-query';
import { ImagesService, ApiError } from '@/api';
import { useAuthContext } from '@/contexts/AuthContext';
import { isDirectImageUrl, isImageId } from '@/utils/imageUtils';

// Query keys for images
export const imageKeys = {
  all: ['images'] as const,
  details: () => [...imageKeys.all, 'detail'] as const,
  detail: (id: string) => [...imageKeys.details(), id] as const,
};

/**
 * Hook to fetch image details by ID, including the download URL
 */
export function useImage(imageId: string | undefined | null) {
  const { isAuthReady } = useAuthContext();
  
  return useQuery({
    queryKey: imageKeys.detail(imageId || ''),
    queryFn: () => ImagesService.getApiV1Images1(imageId!),
    enabled: !!imageId && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes - URLs are valid for longer
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (except 429 - rate limit)
      if (error instanceof ApiError) {
        const status = error.status;
        if (status && status >= 400 && status < 500 && status !== 429) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });
}

/**
 * Helper hook to get just the download URL from an image ID
 */
export function useImageUrl(imageId: string | undefined | null) {
  const { data: image } = useImage(imageId);
  
  return image?.downloadUrl;
}
/**
 * Resolves a stored avatar value to an <img> src: an image id is fetched, a
 * direct URL is used as is, anything else (empty, invalid) gives undefined so
 * the caller shows initials.
 */
export function useAvatarSrc(avatarUrl: string | undefined | null): string | undefined {
  const value = avatarUrl?.trim() || undefined;
  const needsResolution = isImageId(value);
  const resolved = useImageUrl(needsResolution ? value : null);
  if (needsResolution) return resolved || undefined;
  return isDirectImageUrl(value) ? value : undefined;
}
